import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import axios from 'axios';
import { stripCitations } from './moUtils';
import { calculateEffectiveLength } from '../../../lib/dbqTextLimitUtils';

/**
 * Edit Medical Opinion text and apply it to the DBQ Remarks field.
 * The Medical Opinion PDF is deferred until DBQ finalization.
 */
const GenerateMoModal = ({
  show,
  onHide,
  initialText,
  userDbq,
  onApplyToDbq,
}) => {
  const [editedText, setEditedText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [remarksFieldInfo, setRemarksFieldInfo] = useState(null);
  const [loadingRemarksInfo, setLoadingRemarksInfo] = useState(false);

  useEffect(() => {
    if (show) {
      setEditedText(stripCitations(initialText || ''));
      setIsSaving(false);
    }
  }, [show, initialText]);

  useEffect(() => {
    const fetchRemarksFieldInfo = async () => {
      if (!userDbq?.userDbqId) return;

      setLoadingRemarksInfo(true);
      try {
        const response = await axios.get(`/casegpt/mo/remarks-info/${userDbq.userDbqId}`);
        setRemarksFieldInfo(response.data);
      } catch (err) {
        console.error('Failed to fetch remarks field info:', err);
        setRemarksFieldInfo(null);
      } finally {
        setLoadingRemarksInfo(false);
      }
    };

    if (show && userDbq?.userDbqId) {
      fetchRemarksFieldInfo();
    }
  }, [show, userDbq?.userDbqId]);

  const remarksCharLimit = remarksFieldInfo?.maxLength || null;
  const remarksCharsPerLine = remarksFieldInfo?.charsPerLine || null;
  const textLength = editedText?.length || 0;
  const remarksUsage = remarksCharsPerLine
    ? calculateEffectiveLength(editedText || '', remarksCharsPerLine)
    : textLength;
  const exceedsRemarksLimit = remarksCharLimit && remarksUsage > remarksCharLimit;

  const remarksDisabled = loadingRemarksInfo || exceedsRemarksLimit || !remarksFieldInfo?.hasRemarksField;
  const remarksDisabledReason = useMemo(() => {
    if (loadingRemarksInfo) return 'Checking character limits...';
    if (!remarksFieldInfo?.hasRemarksField) return 'This DBQ does not have a Remarks field.';
    if (exceedsRemarksLimit) {
      const unitsLabel = remarksCharsPerLine ? 'effective characters' : 'characters';
      return `Text exceeds the ${remarksCharLimit.toLocaleString()} character limit for the Remarks field (${remarksUsage.toLocaleString()} ${unitsLabel}).`;
    }
    return null;
  }, [
    loadingRemarksInfo,
    remarksFieldInfo,
    exceedsRemarksLimit,
    remarksCharLimit,
    remarksCharsPerLine,
    remarksUsage,
  ]);

  const handleApply = async () => {
    const nextValue = String(editedText || '').trim();
    if (!nextValue) {
      toast.error('Please enter medical opinion text');
      return;
    }

    if (remarksDisabled) {
      toast.error(remarksDisabledReason || 'This text cannot be applied to Remarks.');
      return;
    }

    setIsSaving(true);

    try {
      await Promise.resolve(onApplyToDbq?.(nextValue, remarksFieldInfo?.fieldId));
      toast.success('Medical opinion inserted into Remarks field');
      onHide();
    } catch (err) {
      console.error('Error applying medical opinion to DBQ:', err);
      toast.error('Failed to apply medical opinion to the DBQ');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Medical Opinion</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted mb-3">
          Review and edit the medical opinion text before applying it to the DBQ Remarks field.
          The Medical Opinion PDF is generated later during finalization.
        </p>
        <Form.Group>
          <Form.Control
            as="textarea"
            rows={15}
            value={editedText}
            onChange={(event) => setEditedText(event.target.value)}
            placeholder="Enter medical opinion text..."
            disabled={isSaving}
            style={{
              fontFamily: 'system-ui, -apple-system, sans-serif',
              fontSize: '0.9rem',
              lineHeight: '1.5',
            }}
          />
          <Form.Text className="text-muted mt-2 d-block">
            {textLength.toLocaleString()} characters
            {remarksCharLimit && (
              <span className={exceedsRemarksLimit ? 'text-danger' : ''}>
                {remarksCharsPerLine
                  ? ` | ${remarksUsage.toLocaleString()} / ${remarksCharLimit.toLocaleString()} effective characters for Remarks`
                  : ` / ${remarksCharLimit.toLocaleString()} max for Remarks field`}
              </span>
            )}
          </Form.Text>
        </Form.Group>

        {remarksDisabledReason && (
          <Alert variant="warning" className="mt-3 mb-0">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {remarksDisabledReason}
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleApply}
          disabled={isSaving || !String(editedText || '').trim() || remarksDisabled}
        >
          {isSaving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            'Apply to DBQ Remarks'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GenerateMoModal;
